import { clipEndBar } from '@/audio/clipTiming';
import { classSongMap, formatClassDuration } from '@/state/classSteps';

const EPS = 1e-6;

const eligibleTracks = (tracks = []) => tracks.filter((track) => !track.inBank);

function coveredBars(track, start, end, bpm) {
  const intervals = (track.clips || []).map((clip) => [
    Math.max(start, Number(clip.start_bar || 0)),
    Math.min(end, clipEndBar(clip, bpm)),
  ]).filter(([left, right]) => right > left + EPS).sort((a, b) => a[0] - b[0]);
  let total = 0;
  let cursor = null;
  intervals.forEach(([left, right]) => {
    if (!cursor) { cursor = [left, right]; return; }
    if (left <= cursor[1] + EPS) cursor[1] = Math.max(cursor[1], right);
    else { total += cursor[1] - cursor[0]; cursor = [left, right]; }
  });
  return total + (cursor ? cursor[1] - cursor[0] : 0);
}

const trackEnd = (track, bpm) => (track.clips || []).reduce(
  (end, clip) => Math.max(end, clipEndBar(clip, bpm)), 0,
);

const busiestTrack = (tracks, start, end, bpm) => [...tracks].sort(
  (a, b) => coveredBars(b, start, end, bpm) - coveredBars(a, start, end, bpm),
)[0] || null;

export function classShowMeTarget(step, tracks = [], bpm = 90, classSession = {}) {
  if (!step) return null;
  const map = classSongMap(bpm, 2, classSession.mapId);
  const picked = eligibleTracks(tracks);
  const section = (name) => map.sections.find((item) => item.name === name);
  const base = { kind: step.showMe, toolId: step.tool?.id || null };

  if (step.showMe === 'fly') {
    const shortest = [...picked].sort((a, b) => trackEnd(a, bpm) - trackEnd(b, bpm))[0];
    const clip = [...(shortest?.clips || [])].sort((a, b) => clipEndBar(b, bpm) - clipEndBar(a, bpm))[0];
    return { ...base, targetId: clip ? `clip-${clip.id}` : null, trackId: shortest?.id || null, clipId: clip?.id || null };
  }
  if (step.showMe === 'markers') return { ...base, targetId: 'add-class-markers' };
  if (step.showMe === 'hook' || step.showMe === 'listen') return { ...base, targetId: 'play-pause-btn' };
  if (step.showMe === 'intro') {
    const intro = section('Intro');
    const drums = picked.filter((track) => track.instrument === 'drums');
    const track = busiestTrack(drums.length ? drums : picked, intro.startBar, intro.startBar + intro.bars, bpm);
    return { ...base, targetId: track ? `track-${track.id}-bar-${intro.startBar + intro.bars}` : null, trackId: track?.id || null, bar: intro.startBar + intro.bars };
  }
  if (step.showMe === 'verse') {
    const verse = section('Verse');
    const track = busiestTrack(picked, verse.startBar, verse.startBar + verse.bars, bpm);
    return { ...base, targetId: track ? `track-${track.id}-bar-${verse.startBar}` : null, trackId: track?.id || null, bar: verse.startBar };
  }
  if (step.showMe === 'outro') {
    const outro = section('Outro');
    return { ...base, targetId: 'outro-region', startBar: outro.startBar, endBar: outro.startBar + outro.bars };
  }
  if (step.showMe === 'flavor') {
    const track = picked.find((candidate) => candidate.clips?.length);
    const clip = track?.clips?.[0];
    return { ...base, targetId: clip ? `clip-${clip.id}` : null, trackId: track?.id || null, clipId: clip?.id || null };
  }
  return base;
}

export function classFlyMeter(tracks = [], bpm = 90, classSession = {}) {
  const mapLength = classSongMap(bpm, 2, classSession.mapId).length;
  const picked = eligibleTracks(tracks);
  const ends = picked.map((track) => trackEnd(track, bpm));
  const songSoFar = ends.length ? Math.min(mapLength, Math.min(...ends)) : 0;
  const reached = ends.filter((end) => end >= mapLength - 0.25).length;
  return {
    songSoFar,
    mapLength,
    reached,
    total: picked.length,
    durationText: `Song so far ${formatClassDuration(songSoFar, bpm)} of ${formatClassDuration(mapLength, bpm)}`,
    tracksText: `${reached} of ${picked.length} tracks reach the End`,
  };
}

export function classShowcaseText(mapLength, bpm, complete = true) {
  if (!complete) return null;
  const seconds = Number(mapLength) * 240 / Number(bpm);
  const duration = formatClassDuration(mapLength, bpm);
  return seconds >= 90 && seconds <= 120
    ? `Showcase ready. ${duration} of pure you.`
    : `Your song is ${duration}.`;
}

const TOOL_CHEERS = {
  Copy: "That's the Copy tool. Keep tapping till it hits the End.",
  Slice: "That's the Cut tool. It splits a loop right where you tap.",
  Select: "That's Select and Delete. Tap a piece, hit Delete, it's gone.",
};

export function firstToolCheer(tool, seen) {
  if (!TOOL_CHEERS[tool] || seen.has(tool)) return null;
  seen.add(tool);
  return TOOL_CHEERS[tool];
}
